/**
 * Compact BoM drawer — reviews the active bill of materials from bomStore.
 */
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import {
  bomStore,
  BomLineKind,
  BomLineStatus,
  BOM_CHANGE_EVENT,
  BOM_SAVE_EVENT,
  openCustomerSignIn,
} from '@theme/bom-store';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * @typedef {object} BomDrawerRefs
 * @property {HTMLElement} [badge]
 * @property {HTMLElement} [readOnlyBanner]
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} content
 * @property {HTMLUListElement} lineList
 * @property {HTMLElement} summary
 * @property {HTMLElement} linesCount
 * @property {HTMLElement} subtotal
 * @property {HTMLButtonElement} addAllButton
 * @property {HTMLButtonElement} [shareButton]
 * @property {HTMLElement} [feedback]
 * @property {HTMLTemplateElement} lineTemplate
 */

/**
 * @extends {Component<BomDrawerRefs>}
 */
class BomDrawerComponent extends Component {
  requiredRefs = ['emptyState', 'content', 'lineList', 'summary', 'linesCount', 'subtotal', 'addAllButton', 'lineTemplate'];

  /** @type {string} */
  #currency = 'USD';

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {ReturnType<typeof setTimeout>|null} */
  #feedbackTimer = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#currency = this.dataset.currency ?? 'USD';
    this.#moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';

    document.addEventListener(BOM_CHANGE_EVENT, this.#onStoreChange);
    document.addEventListener(BOM_SAVE_EVENT, this.#onStoreChange);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);

    this.refs.lineList.addEventListener('change', this.#onLineChange);
    this.refs.lineList.addEventListener('click', this.#onLineClick);

    bomStore.init().then(() => this.#render());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(BOM_CHANGE_EVENT, this.#onStoreChange);
    document.removeEventListener(BOM_SAVE_EVENT, this.#onStoreChange);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.refs.lineList?.removeEventListener('change', this.#onLineChange);
    this.refs.lineList?.removeEventListener('click', this.#onLineClick);
    if (this.#feedbackTimer) clearTimeout(this.#feedbackTimer);
  }

  #onDrawerOpen = () => {
    // Close the mobile nav if it was used to open this drawer.
    document.querySelector('pa-header')?.closeMobileDrawer?.();
    this.#render();
  };

  #onStoreChange = () => {
    this.#render();
  };

  /**
   * @param {Event} event
   */
  #onLineChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.dataset.cell !== 'quantity') return;
    const lineId = target.closest('[data-line-id]')?.dataset.lineId;
    if (!lineId || !bomStore.canEdit) return;
    bomStore.updateQuantity(lineId, Number(target.value));
  };

  /**
   * @param {MouseEvent} event
   */
  #onLineClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeButton = target.closest('[data-cell="remove"]');
    if (!(removeButton instanceof HTMLButtonElement)) return;
    const lineId = removeButton.closest('[data-line-id]')?.dataset.lineId;
    if (!lineId || !bomStore.canEdit) return;
    bomStore.removeLine(lineId);
  };

  #render() {
    const lines = bomStore.lines;
    const hasLines = lines.length > 0;
    const readOnly = bomStore.isShareView || !bomStore.canEdit;

    this.refs.emptyState.hidden = hasLines;
    this.refs.content.hidden = !hasLines;
    this.refs.summary.hidden = !hasLines;

    if (this.refs.readOnlyBanner) {
      this.refs.readOnlyBanner.hidden = !readOnly;
    }

    const badge = this.refs.badge || this.querySelector('[data-bom-drawer-badge]');
    if (badge instanceof HTMLElement) {
      badge.textContent = String(lines.length);
      badge.classList.toggle('visually-hidden', lines.length === 0);
    }

    const linesTemplate = this.dataset.textLines || '__COUNT__ lines';
    this.refs.linesCount.textContent = linesTemplate.replaceAll('__COUNT__', String(lines.length));

    let subtotal = 0;
    let hasPricedLines = false;
    for (const line of lines) {
      if (line.price != null && Number.isFinite(line.price)) {
        subtotal += line.price * line.quantity;
        hasPricedLines = true;
      }
    }
    this.refs.subtotal.textContent = hasPricedLines ? this.#formatMoney(subtotal) : '—';

    const addable = lines.filter(
      (line) =>
        (line.status === BomLineStatus.FOUND || line.status === 'found') && line.variantId != null,
    );
    this.refs.addAllButton.disabled = readOnly || addable.length === 0;
    if (this.refs.shareButton) this.refs.shareButton.disabled = false;

    const fragment = document.createDocumentFragment();
    for (const line of lines) {
      fragment.append(this.#buildLineElement(line, readOnly));
    }
    this.refs.lineList.replaceChildren(fragment);
  }

  /**
   * @param {import('@theme/bom-store').BomLine} line
   * @param {boolean} readOnly
   */
  #buildLineElement(line, readOnly) {
    const item = /** @type {HTMLElement} */ (
      this.refs.lineTemplate.content.firstElementChild.cloneNode(true)
    );
    item.dataset.lineId = line.id;

    const displaySku =
      line.kind === BomLineKind.CONFIGURED
        ? line.properties?._catalogNumber || line.sku
        : line.sku;

    this.#setText(item, 'title', line.title || displaySku || '—');
    this.#setText(item, 'sku', displaySku || '—');
    this.#setText(
      item,
      'price',
      line.price != null ? `${this.#formatMoney(line.price)} × ${line.quantity}` : '—',
    );

    const qty = item.querySelector('[data-cell="quantity"]');
    if (qty instanceof HTMLInputElement) {
      qty.value = String(line.quantity);
      qty.disabled = readOnly;
      qty.id = `bom-drawer-qty-${line.id}`;
      const label = item.querySelector('[data-cell="qty-label"]');
      if (label instanceof HTMLLabelElement) label.htmlFor = qty.id;
    }

    const remove = item.querySelector('[data-cell="remove"]');
    if (remove instanceof HTMLButtonElement) {
      remove.hidden = readOnly;
      remove.disabled = readOnly;
    }

    return item;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} cell
   * @param {string} text
   */
  #setText(root, cell, text) {
    const el = root.querySelector(`[data-cell="${cell}"]`);
    if (el) el.textContent = text;
  }

  /** @param {number} value */
  #formatMoney(value) {
    return formatMoney(value, this.#moneyFormat, this.#currency);
  }

  /**
   * @param {string} message
   * @param {'success'|'error'} tone
   */
  #setFeedback(message, tone) {
    const feedback = this.refs.feedback;
    if (!feedback) return;
    feedback.textContent = message;
    feedback.hidden = !message;
    feedback.dataset.tone = tone;
    if (this.#feedbackTimer) clearTimeout(this.#feedbackTimer);
    if (message && tone === 'success') {
      this.#feedbackTimer = setTimeout(() => {
        feedback.hidden = true;
        feedback.textContent = '';
      }, 2500);
    }
  }

  /**
   * Shopify caps line-item property values; chunk long values like the full BoM page.
   * @param {Record<string, string>} properties
   */
  #cartProperties(properties) {
    const chunkSize = 250;
    /** @type {Record<string, string>} */
    const out = {};
    for (const [key, value] of Object.entries(properties)) {
      if (typeof value !== 'string') continue;
      if (value.length <= chunkSize) {
        out[key] = value;
        continue;
      }
      for (let i = 0, chunk = 1; i < value.length; i += chunkSize, chunk++) {
        out[chunk === 1 ? key : `${key}${chunk}`] = value.slice(i, i + chunkSize);
      }
    }
    return out;
  }

  /** @param {Event} event */
  onSignIn(event) {
    event.preventDefault();
    this.#themeDrawer?.close();
    openCustomerSignIn(this.dataset.loginUrl);
  }

  /** @param {Event} event */
  async onShare(event) {
    event.preventDefault();
    const button = this.refs.shareButton;
    if (!button) return;

    button.disabled = true;
    try {
      const url = await bomStore.ensureShareUrl();
      await navigator.clipboard.writeText(url);
      this.#setFeedback(this.dataset.textShareCopied || 'Link copied', 'success');
    } catch (error) {
      console.warn('[bom-drawer] share failed:', error);
      this.#setFeedback(this.dataset.textShareError || 'Could not create a share link.', 'error');
    } finally {
      button.disabled = false;
    }
  }

  /** @param {Event} event */
  async onAddAllToCart(event) {
    event.preventDefault();
    if (!bomStore.canEdit) return;

    const items = bomStore.lines
      .filter(
        (line) =>
          (line.status === BomLineStatus.FOUND || line.status === 'found') && line.variantId != null,
      )
      .map((line) => {
        /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
        const item = {
          id: /** @type {number} */ (line.variantId),
          quantity: line.quantity,
        };
        if (line.kind === BomLineKind.CONFIGURED && line.properties) {
          item.properties = this.#cartProperties(line.properties);
        }
        return item;
      });

    if (items.length === 0) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      }),
    );

    this.refs.addAllButton.disabled = true;
    this.#setFeedback('', 'success');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: data.description,
              errors: data.errors,
            },
          }),
        );

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'bom-drawer',
            itemCount: totalQuantity,
          },
        });

        this.#setFeedback(
          data.description || data.message || this.dataset.textAddError || 'Could not add to cart.',
          'error',
        );
        return;
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bom-drawer',
          itemCount: totalQuantity,
          sections: data.sections,
          didError: false,
        },
      });

      // Close BoM drawer so the cart drawer (auto-open) can take focus.
      this.#themeDrawer?.close();
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        }),
      );
      this.#setFeedback(this.dataset.textAddError || 'Could not add to cart.', 'error');
    } finally {
      this.#render();
    }
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('bom-drawer-component')) {
  customElements.define('bom-drawer-component', BomDrawerComponent);
}
